import React from 'react';
import { cn } from '@/lib/utils';
import type { ValidationError } from './types';

/**
 * Validates that a name contains only letters, hyphens, apostrophes,
 * spaces, and periods. Allows Unicode letters (accented characters like Ñ, é, ü)
 * but rejects HTML tags, SQL injection patterns, emojis, and numbers.
 */
const NAME_PATTERN = /^[\p{Letter}\p{Mark}\s''\-.]+$/u;

export const isValidPersonName = (name: string | null | undefined): boolean => {
  if (!name?.trim()) return true; // empty is handled by required check
  return NAME_PATTERN.test(name.trim());
};

// SQL injection patterns to check for
const SQL_INJECTION_PATTERNS = [
  /(\b(select|insert|update|delete|drop|union|alter|create|where)\b)/i,
  /'.*'/,              // String literals
  /".*"/,              // Double-quoted strings
  /;.*/,               // Multiple statements
  /\/\*.*/,            // Comments
  /--.*/,              // Single line comments
  /\b(or|and)\b.*=.*/i // Boolean operations
];

const containsSQLInjection = (text: string): boolean => {
  return SQL_INJECTION_PATTERNS.some(pattern => pattern.test(text));
};

// Helper function to predict text content after a key press or paste,
const predictNodeTextAfterEdit = (element: HTMLElement, textToInsert: string): string => {
  const currentText = element.textContent || "";
  const selection = window.getSelection();

  if (!selection || selection.rangeCount === 0) {
    return currentText + textToInsert;
  }

  const range = selection.getRangeAt(0);

  if (!element.contains(range.commonAncestorContainer)) {
      return currentText + textToInsert;
  }

  const isSimpleTextSelection = 
    (element.firstChild && range.startContainer === element.firstChild && range.endContainer === element.firstChild && element.firstChild.nodeType === Node.TEXT_NODE) ||
    (!element.firstChild && range.startContainer === element && range.endContainer === element);

  if (isSimpleTextSelection) {
    const start = range.startOffset;
    const end = range.endOffset;
    return currentText.substring(0, start) + textToInsert + currentText.substring(end);
  } else {
    return currentText + textToInsert;
  }
};

export const blockInvalidNumericInput = (
  e: React.KeyboardEvent<HTMLElement>,
  options: { allowDecimal?: boolean; isPhone?: boolean; maxValue?: number | null; allowColon?: boolean; maxDigits?: number | null } = {}
) => {
  const { allowDecimal = false, isPhone = false, maxValue = null, allowColon = false, maxDigits = null } = options;
  const { key, ctrlKey, metaKey } = e;
  const currentTargetElement = e.currentTarget;

  const baseAllowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
  const digitKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
  const allowedKeysSet = new Set([...baseAllowedKeys, ...digitKeys]);

  if (isPhone) {
    // For phone numbers, allow specific special characters. Decimals are not typically allowed.
    ['+', '-', '(', ')', ' ', '#'].forEach(k => allowedKeysSet.add(k));
  } else if (allowDecimal) {
    // Allow decimal point only if not a phone input and allowDecimal is true
    allowedKeysSet.add('.');
  }

  if (allowColon) {
    allowedKeysSet.add(':');
  }

  if (ctrlKey || metaKey) {
    if (['a', 'c', 'v', 'x', 'z', 'y'].includes(key.toLowerCase())) {
      return; // Allow common shortcuts
    }
  }

  if (!allowedKeysSet.has(key)) {
    e.preventDefault();
    return;
  }

  const inputValue = (currentTargetElement instanceof HTMLInputElement) ? currentTargetElement.value : (currentTargetElement.textContent || "");

  // Specific check for decimal point if allowed and not for phone
  if (allowDecimal && !isPhone && key === '.') {
    if (inputValue.includes('.')) {
      e.preventDefault();
      return;
    }
  }

  // Max digits check when a digit is pressed
  if (digitKeys.includes(key) && maxDigits != null && maxDigits > 0) {
    if (currentTargetElement instanceof HTMLInputElement) {
      const currentText = currentTargetElement.value;
      const selectionStart = currentTargetElement.selectionStart || 0;
      const selectionEnd = currentTargetElement.selectionEnd || 0;
      
      // Calculate what the text would be after the keystroke
      const textAfterEdit = currentText.substring(0, selectionStart) + key + currentText.substring(selectionEnd);
      const digitCountAfterEdit = (textAfterEdit.match(/\d/g) || []).length;
      
      if (digitCountAfterEdit > maxDigits) {
        e.preventDefault();
        return;
      }
    } else if (currentTargetElement instanceof HTMLElement) {
      // For contentEditable elements, use the existing predictNodeTextAfterEdit logic
      const potentialText = predictNodeTextAfterEdit(currentTargetElement, key);
      const digitCount = (potentialText.match(/\d/g) || []).length;
      if (digitCount > maxDigits) {
        e.preventDefault();
        return;
      }
    }
  }

  // Max value check for integer input type (allowDecimal is false, not a phone input) when a digit is pressed
  if (!allowDecimal && !isPhone && digitKeys.includes(key) && maxValue != null && maxValue > 0) {
    if (currentTargetElement instanceof HTMLElement) { // Ensure it's an HTMLElement for predictNodeTextAfterEdit
        const potentialText = predictNodeTextAfterEdit(currentTargetElement, key);
        const potentialNum = parseInt(potentialText, 10);
        if (!isNaN(potentialNum) && potentialNum > maxValue) {
            e.preventDefault();
            return;
        }
    }
  }
};

export const handleNumericPaste = (
  e: React.ClipboardEvent<HTMLElement>,
  options: { allowDecimal?: boolean; isPhone?: boolean; maxValue?: number | null; maxDigits?: number | null } = {}
) => {
  const { allowDecimal = false, isPhone = false, maxValue = null, maxDigits = null } = options;
  const pastedText = e.clipboardData.getData('text');
  const currentTargetElement = e.currentTarget;

  if (containsSQLInjection(pastedText)) {
    e.preventDefault();
    return;
  }

  if (currentTargetElement instanceof HTMLInputElement) {
    // Logic for HTMLInputElements (try to clean and insert)
    let cleanedText = pastedText;
    let needsUpdate = false;

    if (isPhone) {
      cleanedText = pastedText.replace(/[^\d()+-\s#]/g, '');
      if (cleanedText !== pastedText) needsUpdate = true;
    } else if (allowDecimal) {
      cleanedText = pastedText.replace(/[^\d.]/g, '');
      const decimalParts = cleanedText.split('.');
      if (decimalParts.length > 1) {
        cleanedText = decimalParts[0] + '.' + decimalParts.slice(1).join('');
      }
      if (cleanedText !== pastedText || !/^\d*\.?\d*$/.test(cleanedText)) needsUpdate = true;
      if (!/^\d*\.?\d*$/.test(cleanedText)) cleanedText = ""; // Invalid format after cleaning
    } else { // Integer input
      cleanedText = pastedText.replace(/\D/g, '');
      if (cleanedText !== pastedText) needsUpdate = true;
    }

    if (needsUpdate) e.preventDefault();

    if (cleanedText.length > 0) {
      // Check maxDigits before attempting to update
      if (maxDigits != null && maxDigits > 0) {
        const digitCount = (cleanedText.match(/\d/g) || []).length;
        if (digitCount > maxDigits) {
          return; // Don't insert if cleaned text exceeds max digits
        }
      }

      // For integers, check maxValue before attempting to update
      if (!isPhone && !allowDecimal && maxValue != null && maxValue > 0) {
        const num = parseInt(cleanedText, 10);
        if (!isNaN(num) && num > maxValue) {
          return; // Don't insert if cleaned text itself exceeds max value
        }
      }
      
      // Simplified: update the value. This matches original behavior.
      // More complex logic would be needed to precisely manage cursor/selection.
      const start = currentTargetElement.selectionStart || 0;
      const end = currentTargetElement.selectionEnd || 0;
      const originalValue = currentTargetElement.value;
      const proposedValue = originalValue.substring(0, start) + cleanedText + originalValue.substring(end);

      // Final validation of the proposed value for integers with maxValue
      if (!isPhone && !allowDecimal && maxValue != null && maxValue > 0) {
          if (/^\d+$/.test(proposedValue)) {
              const num = parseInt(proposedValue, 10);
              if (!isNaN(num) && num > maxValue) return; // Block update
          } else {
              return; // Block if not a valid integer string after paste
          }
      }
      
      currentTargetElement.value = proposedValue;
      // Consider dispatching a synthetic 'input' event if React state doesn't update
      // currentTargetElement.dispatchEvent(new Event('input', { bubbles: true }));
    } else if (needsUpdate) { // Pasted text was invalid and cleaned to empty
        const start = currentTargetElement.selectionStart || 0;
        const end = currentTargetElement.selectionEnd || 0;
        currentTargetElement.value = currentTargetElement.value.substring(0, start) + "" + currentTargetElement.value.substring(end);
    }
    // If no cleaning was needed (cleanedText === pastedText), let browser handle default paste.
    // Then apply maxValue check if applicable.
    if (!needsUpdate && !isPhone && !allowDecimal && maxValue != null && maxValue > 0) {
        const val = currentTargetElement.value;
        const start = currentTargetElement.selectionStart ?? 0;
        const end = currentTargetElement.selectionEnd ?? 0;
        const finalValueStr = val.substring(0, start) + pastedText + val.substring(end);
        if (/^\d+$/.test(finalValueStr)) {
            const potentialNum = parseInt(finalValueStr, 10);
            if (!isNaN(potentialNum) && potentialNum > maxValue) {
                e.preventDefault();
            }
        } else {
             e.preventDefault(); // Pasting non-digits into integer field
        }
    }

  } else if (currentTargetElement instanceof HTMLElement && currentTargetElement.isContentEditable) {
    // Logic for contentEditable elements (typically integers like ReminderChip)
    // Validate pasted text; if not perfect, prevent default. Don't try to clean and insert.
    if (isPhone) { // Unlikely for contentEditable in this app, but for completeness
        if (!/^[+\d()\s#-]*$/.test(pastedText)) e.preventDefault();
        return;
    }
    if (allowDecimal) { // Also unlikely for current contentEditable use cases
        if (!/^\d*\.?\d*$/.test(pastedText) || (pastedText.match(/\./g) || []).length > 1) e.preventDefault();
        return;
    }
    
    // Integer handling for contentEditable
    if (!/^\d+$/.test(pastedText)) {
      e.preventDefault();
      return;
    }
    if (maxValue != null && maxValue > 0) {
      const potentialText = predictNodeTextAfterEdit(currentTargetElement, pastedText);
      if (/^\d+$/.test(potentialText)) {
        const potentialNum = parseInt(potentialText, 10);
        if (!isNaN(potentialNum) && potentialNum > maxValue) {
          e.preventDefault();
        }
      } else { // predictNodeTextAfterEdit resulted in non-numeric string
        e.preventDefault();
      }
    }
  }
};


export const formatPhoneNumber = (value: string): string => {
  // Remove all non-digit characters
  const numbers = value.replace(/\D/g, '');
  const length = numbers.length;
  
  // Auto-format based on length
  if (length <= 3) return numbers;
  if (length <= 6) return `(${numbers.slice(0,3)}) ${numbers.slice(3)}`;
  if (length <= 10) return `(${numbers.slice(0,3)}) ${numbers.slice(3,6)}-${numbers.slice(6,10)}`;
  return numbers; // Return unformatted if longer than 10 digits (international numbers)
};

export const validateDomain = (domain: string): string | null => {
  if (!domain) return null; // Allow empty domain
  
  // Remove protocol and www if present
  const cleanDomain = domain.replace(/^(https?:\/\/)?(www\.)?/, '');
  
  // Domain validation regex that allows subdomains and paths
  const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(?:\/[^\s]*)?$/;
  
  if (!domainRegex.test(cleanDomain)) {
    return 'Please enter a valid domain (e.g., example.com)';
  }
  
  return null;
};

export const getErrorMessage = (
  fieldName: string,
  errors: ValidationError[] = [],
  touchedFields: Set<string> = new Set()
): string | null => {
  if (!touchedFields.has(fieldName)) return null;
  return errors.find(e => e.field === fieldName)?.message || null;
};

export const getFieldErrorElement = (
  fieldName: string,
  errors: ValidationError[] = [],
  touchedFields: Set<string> = new Set(),
  className?: string
): React.ReactElement | null => {
  const message = getErrorMessage(fieldName, errors, touchedFields);
  if (!message) return null;
  return React.createElement('p', {
    className: cn("text-xs text-destructive mt-1 whitespace-nowrap text-left sm:text-right", className)
  }, message);
};
